import React, { useState, useEffect, useCallback, useRef } from 'react';
import { formatBrandMoneyCompact, getBrandCurrencySuffix } from '../../shared/utils/brandCurrency';
import BrandCurrencySelect from '../../shared/components/BrandCurrencySelect';
import { motion } from 'framer-motion';
import {
  Settings,
  Bell,
  Landmark,
  Shield,
  FileCheck,
  CreditCard,
  DollarSign,
  RefreshCcw,
  Plug,
  AlertTriangle,
  Hash,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import localApiAdapter from '../../core/services/localApiAdapter';
import ModuleConfigPageLayout from '../../shared/components/settings/ModuleConfigPageLayout';
import { mapConfigFromApi, mapConfigToApi } from './bankingApiMappers';
import { syncBanqueConfigToSettings } from './useBanqueConfig';
import { useSettings } from '../../core/contexts/SettingsContext';
import {
  SAUDI_BANKS,
  SAUDI_TRANSFER_SYSTEMS,
  SAUDI_IBAN_PLACEHOLDER,
  SAUDI_DEFAULT_BANK_ACCOUNTS,
  normalizeSaudiIban,
  validateSaudiIban,
} from './saudiBankingDefaults';

const DEFAULT_BANKING_CONFIG = mapConfigFromApi({});

const GLASS_CARD = 'bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20';

const SectionCard = ({ icon: Icon, iconClassName, title, children }) => (
  <div className={GLASS_CARD}>
    <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
      <Icon className={`h-6 w-6 ${iconClassName}`} />
      {title}
    </h2>
    {children}
  </div>
);

const BankingConfigPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.banque.pages.bankingConfigPage.${key}`, opts);
  const { patchSettings, settings } = useSettings();
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [apiConfig, setApiConfig] = useState({});
  const [bankingConfig, setBankingConfig] = useState(DEFAULT_BANKING_CONFIG);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await localApiAdapter.get('/banking/config');
      const merged = mapConfigFromApi(data || {});
      setApiConfig(data || {});
      setBankingConfig(merged);
      syncBanqueConfigToSettings(merged, patchSettings, data || {}, settingsRef.current);
    } catch (error) {
      console.error('Error loading banking config:', error);
      toast.error(error.message || p('load_error'));
      setApiConfig({});
      setBankingConfig(DEFAULT_BANKING_CONFIG);
      syncBanqueConfigToSettings(DEFAULT_BANKING_CONFIG, patchSettings, {}, settingsRef.current);
    } finally {
      setLoading(false);
    }
  }, [p, patchSettings]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    const ibanCheck = validateSaudiIban(bankingConfig.defaultBankAccount);
    if (bankingConfig.defaultBankAccount && !ibanCheck.valid) {
      toast.error(ibanCheck.message || p('iban_invalid'));
      return;
    }
    setSaving(true);
    try {
      const payload = mapConfigToApi(bankingConfig, apiConfig);
      await localApiAdapter.put('/banking/config', payload);
      setApiConfig(payload);
      syncBanqueConfigToSettings(bankingConfig, patchSettings, payload, settingsRef.current);
      setSaved(true);
      toast.success(p('saved_success'));
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving banking config:', error);
      toast.error(error.message || p('save_error'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(p('reset_confirm'))) return;
    try {
      const data = await localApiAdapter.post('/banking/config/reset', {});
      const resetConfig = mapConfigFromApi(data || {});
      setApiConfig(data || {});
      setBankingConfig(resetConfig);
      syncBanqueConfigToSettings(resetConfig, patchSettings, data || {}, settingsRef.current);
      toast.success(p('reset_success'));
    } catch (error) {
      console.error('Error resetting banking config:', error);
      toast.error(error.message || p('reset_error'));
    }
  };

  const updateConfig = (key, value) => {
    setBankingConfig((prev) => {
      const next = { ...prev, [key]: value };
      syncBanqueConfigToSettings(next, patchSettings, apiConfig, settingsRef.current);
      return next;
    });
  };

  const toggleTransferSystem = (system) => {
    const current = bankingConfig.enabledTransferSystems || [];
    const next = current.includes(system)
      ? current.filter((s) => s !== system)
      : [...current, system];
    updateConfig('enabledTransferSystems', next.length ? next : current);
  };

  const applyDefaultAccount = (account) => {
    setBankingConfig((prev) => ({
      ...prev,
      defaultBankAccount: account.iban,
      defaultBankCode: account.code,
    }));
  };

  const currencySuffix = getBrandCurrencySuffix();

  const stats = {
    maxTransfer: formatBrandMoneyCompact(bankingConfig.maxTransferAmount),
    approvalLevels: bankingConfig.transferApprovalLevels,
    checkDays: bankingConfig.checkClearanceDays,
    security: bankingConfig.requireTwoFactorAuth ? p('security_high') : p('security_standard'),
    notifications: [bankingConfig.notifyOnTransfer, bankingConfig.notifyOnCheckCleared, bankingConfig.notifyOnPaymentOrder, bankingConfig.notifyOnFees].filter(Boolean).length,
  };

  const statCards = [
    { title: p('stat_max_transfer'), value: stats.maxTransfer, icon: CreditCard, color: 'from-blue-500 to-blue-600' },
    { title: p('stat_approval_levels'), value: stats.approvalLevels, icon: Shield, color: 'from-purple-500 to-purple-600' },
    { title: p('stat_check_days'), value: p('days_unit', { count: stats.checkDays }), icon: FileCheck, color: 'from-amber-500 to-amber-600' },
    { title: p('stat_security'), value: stats.security, icon: Shield, color: 'from-red-500 to-red-600' },
  ];

  const generalTabContent = (
    <>
      <SectionCard icon={Landmark} iconClassName="text-blue-400" title={p('section_general')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_default_currency')}</label>
            <BrandCurrencySelect
              value={bankingConfig.defaultCurrency}
              onChange={(e) => updateConfig('defaultCurrency', e.target.value)}
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_default_bank')}</label>
            <select
              value={bankingConfig.defaultBankCode || ''}
              onChange={(e) => {
                const bank = SAUDI_BANKS.find((b) => b.code === e.target.value);
                updateConfig('defaultBankCode', e.target.value);
                if (bank) updateConfig('defaultBankAccount', bankingConfig.defaultBankAccount);
              }}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {SAUDI_BANKS.map((bank) => (
                <option key={bank.code} value={bank.code} className="bg-gray-800">{bank.name}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_default_account')}</label>
            <input
              type="text"
              value={bankingConfig.defaultBankAccount}
              onChange={(e) => updateConfig('defaultBankAccount', normalizeSaudiIban(e.target.value))}
              placeholder={SAUDI_IBAN_PLACEHOLDER}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-white/50 text-xs mt-1">{p('hint_iban_format')}</p>
          </div>
          <div className="md:col-span-2 flex flex-wrap gap-2">
            {SAUDI_DEFAULT_BANK_ACCOUNTS.map((acct) => (
              <button
                key={acct.iban}
                type="button"
                onClick={() => applyDefaultAccount(acct)}
                className="text-xs bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 px-3 py-1.5 rounded-lg transition-colors"
              >
                {acct.name}
              </button>
            ))}
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_statement_import_frequency')}</label>
            <select value={bankingConfig.statementImportFrequency} onChange={(e) => updateConfig('statementImportFrequency', e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="daily">{p('import_freq_daily')}</option>
              <option value="weekly">{p('import_freq_weekly')}</option>
              <option value="monthly">{p('import_freq_monthly')}</option>
            </select>
          </div>
          <div className="flex items-center">
            <input type="checkbox" checked={bankingConfig.autoImportStatements} onChange={(e) => updateConfig('autoImportStatements', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
            <label className="ml-3 text-white/80">{p('check_auto_import_statements')}</label>
          </div>
        </div>
      </SectionCard>

      <SectionCard icon={Plug} iconClassName="text-cyan-400" title={p('section_integration')}>
        <div className="space-y-4">
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_transfer_systems')}</label>
            <div className="flex flex-wrap gap-3">
              {SAUDI_TRANSFER_SYSTEMS.map((sys) => (
                <label key={sys.value} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-4 py-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={(bankingConfig.enabledTransferSystems || []).includes(sys.value)}
                    onChange={() => toggleTransferSystem(sys.value)}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span className="text-white/80 text-sm">{sys.labelAr}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center">
              <input type="checkbox" checked={bankingConfig.sarieInstantTransfer} onChange={(e) => updateConfig('sarieInstantTransfer', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
              <label className="ml-3 text-white/80">{p('check_sarie_instant')}</label>
            </div>
            <div className="flex items-center">
              <input type="checkbox" checked={bankingConfig.sadadBillPayment} onChange={(e) => updateConfig('sadadBillPayment', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
              <label className="ml-3 text-white/80">{p('check_sadad_bills')}</label>
            </div>
            <div className="flex items-center">
              <input type="checkbox" checked={bankingConfig.bankApiIntegration} onChange={(e) => updateConfig('bankApiIntegration', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
              <label className="ml-3 text-white/80">{p('check_bank_api')}</label>
            </div>
            <div>
              <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_statement_format')}</label>
              <select value={bankingConfig.statementImportFormat} onChange={(e) => updateConfig('statementImportFormat', e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="saudi_csv">{p('format_saudi_csv')}</option>
                <option value="saudi_mt940">{p('format_mt940')}</option>
                <option value="snb_export">{p('format_snb')}</option>
                <option value="rajhi_export">{p('format_rajhi')}</option>
              </select>
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard icon={Hash} iconClassName="text-indigo-400" title={p('section_reference_prefixes')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_transfer_reference_prefix')}</label>
            <input
              type="text"
              value={bankingConfig.transferReferencePrefix || ''}
              onChange={(e) => updateConfig('transferReferencePrefix', e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_check_reference_prefix')}</label>
            <input
              type="text"
              value={bankingConfig.checkReferencePrefix || ''}
              onChange={(e) => updateConfig('checkReferencePrefix', e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_statement_reference_prefix')}</label>
            <input
              type="text"
              value={bankingConfig.statementReferencePrefix || ''}
              onChange={(e) => updateConfig('statementReferencePrefix', e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </SectionCard>
    </>
  );

  const operationsTabContent = (
    <>
      <SectionCard icon={CreditCard} iconClassName="text-blue-400" title={p('section_transfers')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_max_transfer')} ({currencySuffix})</label>
            <input type="number" value={bankingConfig.maxTransferAmount} onChange={(e) => updateConfig('maxTransferAmount', parseInt(e.target.value, 10))} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_approval_above')} ({currencySuffix})</label>
            <input type="number" value={bankingConfig.requireApprovalAbove} onChange={(e) => updateConfig('requireApprovalAbove', parseInt(e.target.value, 10))} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_approval_levels')}</label>
            <select value={bankingConfig.transferApprovalLevels} onChange={(e) => updateConfig('transferApprovalLevels', parseInt(e.target.value))} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="1">{p('approval_level_1')}</option>
              <option value="2">{p('approval_level_2')}</option>
              <option value="3">{p('approval_level_3')}</option>
            </select>
          </div>
          <div className="flex items-center">
            <input type="checkbox" checked={bankingConfig.allowInternationalTransfers} onChange={(e) => updateConfig('allowInternationalTransfers', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
            <label className="ml-3 text-white/80">{p('check_allow_international')}</label>
          </div>
        </div>
      </SectionCard>

      <SectionCard icon={FileCheck} iconClassName="text-amber-400" title={p('section_checks')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_check_clearance_days')}</label>
            <input type="number" value={bankingConfig.checkClearanceDays} onChange={(e) => updateConfig('checkClearanceDays', parseInt(e.target.value))} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_expiring_checks_days')}</label>
            <input type="number" value={bankingConfig.expiringChecksDays} onChange={(e) => updateConfig('expiringChecksDays', parseInt(e.target.value))} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_max_check_amount')} ({currencySuffix})</label>
            <input type="number" value={bankingConfig.maxCheckAmount} onChange={(e) => updateConfig('maxCheckAmount', parseInt(e.target.value, 10))} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex items-center">
            <input type="checkbox" checked={bankingConfig.alertExpiringChecks} onChange={(e) => updateConfig('alertExpiringChecks', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
            <label className="ml-3 text-white/80">{p('check_alert_expiring_checks')}</label>
          </div>
        </div>
      </SectionCard>

      <SectionCard icon={FileCheck} iconClassName="text-green-400" title={p('section_payments')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_order_execution_time')}</label>
            <input type="time" value={bankingConfig.orderExecutionTime} onChange={(e) => updateConfig('orderExecutionTime', e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex items-center">
            <input type="checkbox" checked={bankingConfig.autoExecuteOrders} onChange={(e) => updateConfig('autoExecuteOrders', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
            <label className="ml-3 text-white/80">{p('check_auto_execute_orders')}</label>
          </div>
          <div className="flex items-center">
            <input type="checkbox" checked={bankingConfig.requireDualApproval} onChange={(e) => updateConfig('requireDualApproval', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
            <label className="ml-3 text-white/80">{p('check_require_dual_approval')}</label>
          </div>
        </div>
      </SectionCard>
    </>
  );

  const reconciliationTabContent = (
    <>
      <SectionCard icon={Shield} iconClassName="text-purple-400" title={p('section_reconciliation')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_reconciliation_tolerance')} ({currencySuffix})</label>
            <input type="number" value={bankingConfig.reconciliationTolerance} onChange={(e) => updateConfig('reconciliationTolerance', parseInt(e.target.value, 10))} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_reconciliation_frequency')}</label>
            <select value={bankingConfig.reconciliationFrequency} onChange={(e) => updateConfig('reconciliationFrequency', e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="daily">{p('freq_daily')}</option>
              <option value="weekly">{p('freq_weekly')}</option>
              <option value="monthly">{p('freq_monthly')}</option>
            </select>
          </div>
          <div className="flex items-center">
            <input type="checkbox" checked={bankingConfig.autoReconciliation} onChange={(e) => updateConfig('autoReconciliation', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
            <label className="ml-3 text-white/80">{p('check_auto_reconciliation')}</label>
          </div>
          <div className="flex items-center">
            <input type="checkbox" checked={bankingConfig.alertOnMismatch} onChange={(e) => updateConfig('alertOnMismatch', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
            <label className="ml-3 text-white/80">{p('check_alert_on_mismatch')}</label>
          </div>
        </div>
      </SectionCard>

      <SectionCard icon={DollarSign} iconClassName="text-red-400" title={p('section_fees')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_fee_threshold')} ({currencySuffix})</label>
            <input type="number" value={bankingConfig.feeThreshold} onChange={(e) => updateConfig('feeThreshold', parseInt(e.target.value, 10))} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex items-center">
            <input type="checkbox" checked={bankingConfig.trackBankFees} onChange={(e) => updateConfig('trackBankFees', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
            <label className="ml-3 text-white/80">{p('check_track_bank_fees')}</label>
          </div>
          <div className="flex items-center">
            <input type="checkbox" checked={bankingConfig.alertOnHighFees} onChange={(e) => updateConfig('alertOnHighFees', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
            <label className="ml-3 text-white/80">{p('check_alert_on_high_fees')}</label>
          </div>
        </div>
      </SectionCard>

      <SectionCard icon={AlertTriangle} iconClassName="text-orange-400" title={p('section_alerts')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_low_balance_threshold')} ({currencySuffix})</label>
            <input type="number" value={bankingConfig.lowBalanceThreshold} onChange={(e) => updateConfig('lowBalanceThreshold', parseInt(e.target.value, 10))} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_high_transaction_threshold')} ({currencySuffix})</label>
            <input type="number" value={bankingConfig.highTransactionThreshold} onChange={(e) => updateConfig('highTransactionThreshold', parseInt(e.target.value, 10))} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_unusual_activity_threshold')} ({currencySuffix})</label>
            <input type="number" value={bankingConfig.unusualActivityThreshold} onChange={(e) => updateConfig('unusualActivityThreshold', parseInt(e.target.value, 10))} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
        </div>
      </SectionCard>
    </>
  );

  const preferencesTabContent = (
    <>
      <SectionCard icon={Bell} iconClassName="text-yellow-400" title={p('section_notifications')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="flex items-center">
            <input type="checkbox" checked={bankingConfig.notifyOnTransfer} onChange={(e) => updateConfig('notifyOnTransfer', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
            <label className="ml-3 text-white/80">{p('check_notify_transfer')}</label>
          </div>
          <div className="flex items-center">
            <input type="checkbox" checked={bankingConfig.notifyOnCheckCleared} onChange={(e) => updateConfig('notifyOnCheckCleared', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
            <label className="ml-3 text-white/80">{p('check_notify_check_cleared')}</label>
          </div>
          <div className="flex items-center">
            <input type="checkbox" checked={bankingConfig.notifyOnPaymentOrder} onChange={(e) => updateConfig('notifyOnPaymentOrder', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
            <label className="ml-3 text-white/80">{p('check_notify_payment_order')}</label>
          </div>
          <div className="flex items-center">
            <input type="checkbox" checked={bankingConfig.notifyOnFees} onChange={(e) => updateConfig('notifyOnFees', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
            <label className="ml-3 text-white/80">{p('check_notify_fees')}</label>
          </div>
          <div className="flex items-center">
            <input type="checkbox" checked={bankingConfig.emailNotifications} onChange={(e) => updateConfig('emailNotifications', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
            <label className="ml-3 text-white/80">{p('check_email_notifications')}</label>
          </div>
          <div className="flex items-center">
            <input type="checkbox" checked={bankingConfig.smsNotifications} onChange={(e) => updateConfig('smsNotifications', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
            <label className="ml-3 text-white/80">{p('check_sms_notifications')}</label>
          </div>
        </div>
      </SectionCard>

      <SectionCard icon={Shield} iconClassName="text-red-400" title={p('section_security')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_session_timeout')}</label>
            <input type="number" value={bankingConfig.sessionTimeout} onChange={(e) => updateConfig('sessionTimeout', parseInt(e.target.value))} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex items-center">
            <input type="checkbox" checked={bankingConfig.requireTwoFactorAuth} onChange={(e) => updateConfig('requireTwoFactorAuth', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
            <label className="ml-3 text-white/80">{p('check_require_two_factor')}</label>
          </div>
          <div className="flex items-center">
            <input type="checkbox" checked={bankingConfig.encryptData} onChange={(e) => updateConfig('encryptData', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
            <label className="ml-3 text-white/80">{p('check_encrypt_data')}</label>
          </div>
          <div className="flex items-center">
            <input type="checkbox" checked={bankingConfig.logAllOperations} onChange={(e) => updateConfig('logAllOperations', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
            <label className="ml-3 text-white/80">{p('check_log_all_operations')}</label>
          </div>
        </div>
      </SectionCard>

      <SectionCard icon={Settings} iconClassName="text-indigo-400" title={p('section_display')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_default_view')}</label>
            <select value={bankingConfig.defaultView} onChange={(e) => updateConfig('defaultView', e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="dashboard">{p('view_dashboard')}</option>
              <option value="accounts">{p('view_accounts')}</option>
              <option value="statements">{p('view_statements')}</option>
            </select>
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_date_format')}</label>
            <select value={bankingConfig.dateFormat} onChange={(e) => updateConfig('dateFormat', e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            </select>
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_items_per_page')}</label>
            <select value={bankingConfig.itemsPerPage} onChange={(e) => updateConfig('itemsPerPage', parseInt(e.target.value))} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
          <div className="flex items-center">
            <input type="checkbox" checked={bankingConfig.showBalanceChart} onChange={(e) => updateConfig('showBalanceChart', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
            <label className="ml-3 text-white/80">{p('check_show_balance_chart')}</label>
          </div>
        </div>
      </SectionCard>
    </>
  );

  const moduleTabs = [
    { id: 'general', label: p('tab_general'), icon: Landmark, gradient: 'from-blue-500 to-blue-600' },
    { id: 'operations', label: p('tab_operations'), icon: CreditCard, gradient: 'from-teal-500 to-teal-600' },
    { id: 'reconciliation', label: p('tab_reconciliation'), icon: Shield, gradient: 'from-purple-500 to-purple-600' },
    { id: 'preferences', label: p('tab_preferences'), icon: Bell, gradient: 'from-amber-500 to-amber-600' },
  ];

  const tabContent = {
    general: generalTabContent,
    operations: operationsTabContent,
    reconciliation: reconciliationTabContent,
    preferences: preferencesTabContent,
  };

  return (
    <ModuleConfigPageLayout
      title={p('title')}
      subtitle={p('subtitle')}
      titleIcon={Settings}
      tabs={moduleTabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      tabContent={tabContent}
      saved={saved}
      savedMessage={p('saved_success')}
      loading={loading}
      saving={saving}
      onSave={handleSave}
      onReset={handleReset}
      headerActions={(
        <button
          type="button"
          onClick={loadConfig}
          className="refresh-button px-3 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-colors"
          disabled={loading}
          aria-label={t('common.refresh', 'Refresh')}
        >
          <RefreshCcw size={20} className={loading ? 'animate-spin' : ''} />
        </button>
      )}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, i) => (
          <motion.div key={stat.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} />
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`} />
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white force-white" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </ModuleConfigPageLayout>
  );
};

export default BankingConfigPage;
